/**
 * Pull the real reason out of a failed API call.
 *
 * The backend reports errors in several shapes and the UI used to ignore all of
 * them, showing one fixed sentence per call site — so a validation failure, a
 * permission problem and a server crash all read the same, usually wrong,
 * message ("tracking number is already added" for any failure at all).
 *
 * Shapes handled:
 *   {"error": "not your address"}                     → the string
 *   {"error": {"tracking_number": ["...", "..."]}}    → Laravel validator bag
 *   {"errors": ["...", "..."]}                        → our OnWay endpoints
 *   {"message": "...", "errors": {...}}               → Laravel's own 422
 *   "plain text body"                                 → as-is
 *
 * `fallback` is what the call site used to show; it is used only when the
 * response carries nothing readable, so behaviour never gets worse.
 */
export const apiError = (err, fallback = "") => {
  // No response at all — request never made it, or the connection dropped.
  if (err && err.request && !err.response) {
    return fallback || "კავშირი ვერ დამყარდა — შეამოწმეთ ინტერნეტი";
  }

  const res = err?.response;
  const data = res?.data;

  const flatten = (value, depth = 0) => {
    if (value == null || depth > 3) return [];
    if (typeof value === "string") return value.trim() ? [value.trim()] : [];
    if (typeof value === "number" || typeof value === "boolean") return [String(value)];
    if (Array.isArray(value)) return value.flatMap((v) => flatten(v, depth + 1));
    if (typeof value === "object") {
      return Object.values(value).flatMap((v) => flatten(v, depth + 1));
    }
    return [];
  };

  if (typeof data === "string" && data.trim()) {
    // Laravel debug pages come back as HTML — never show that to an operator.
    if (!/^\s*</.test(data)) return data.trim();
  }

  if (data && typeof data === "object") {
    // `error` first: it's what this codebase uses most, and it's the specific one.
    const parts = [
      ...flatten(data.error),
      ...flatten(data.errors),
    ];
    if (parts.length) return [...new Set(parts)].join(" · ");

    if (typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
  }

  // Nothing readable in the body — say something truer than a fixed sentence.
  if (res?.status === 401 || res?.status === 542) {
    return fallback || "სესია ამოიწურა — გაიარეთ ავტორიზაცია თავიდან";
  }
  if (res?.status === 403) return fallback || "წვდომა აკრძალულია";
  if (res?.status >= 500) {
    return fallback ? `${fallback} (სერვერის შეცდომა ${res.status})` : `სერვერის შეცდომა ${res.status}`;
  }

  return fallback || (res?.status ? `შეცდომა (${res.status})` : "შეცდომა");
};

export default apiError;
